import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

const VerticalSlider = ({ sliders, setActiveText }) => {
  const trackRef = useRef(null);
  const [activeTextState, setActiveTextState] = useState(sliders[0]?.title || '');
  const [currentIndex, setCurrentIndex] = useState(sliders.length);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const itemHeight = 90;

  // Duplicate sliders for infinite scrolling effect
  const allItems = [...sliders, ...sliders, ...sliders];

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 767);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleButtonClick = (text) => {
    setActiveTextState(text); // Update local active text
    setActiveText(text); // Update parent component
  };

  const updatePosition = (index) => {
    const track = trackRef.current;
    if (!track) return;

    track.style.transition = isTransitioning ? 'transform 0.5s ease' : 'none';

    if (isMobile) {
      const itemWidth = track.offsetWidth / 2;
      track.style.transform = `translateX(-${index * (itemWidth + 20)}px)`;
    } else {
      track.style.transform = `translateY(-${index * itemHeight}px)`;
    }
  };

  const handleTransitionEnd = () => {
    const track = trackRef.current;
    if (!track) return;

    if (currentIndex >= allItems.length - sliders.length) {
      setIsTransitioning(false);
      setCurrentIndex(sliders.length);
      updatePosition(sliders.length);
    } else if (currentIndex <= sliders.length - 1) {
      setIsTransitioning(false);
      setCurrentIndex(allItems.length - sliders.length * 2);
      updatePosition(allItems.length - sliders.length * 2);
    }
  };

  const scrollUp = () => {
    setIsTransitioning(true);
    setCurrentIndex((prev) => {
      const newIndex = prev - 1;
      updatePosition(newIndex);
      // Update active text based on the new index
      const newItemIndex = (newIndex - sliders.length) % sliders.length;
      const newText = sliders[newItemIndex >= 0 ? newItemIndex : sliders.length + newItemIndex]?.title || sliders[0]?.title || '';
      handleButtonClick(newText);
      return newIndex;
    });
  };

  const scrollDown = () => {
    setIsTransitioning(true);
    setCurrentIndex((prev) => {
      const newIndex = prev + 1;
      updatePosition(newIndex);
      // Update active text based on the new index
      const newItemIndex = (newIndex - sliders.length) % sliders.length;
      const newText = sliders[newItemIndex >= 0 ? newItemIndex : sliders.length + newItemIndex]?.title || sliders[0]?.title || '';
      handleButtonClick(newText);
      return newIndex;
    });
  };

  useEffect(() => {
    const track = trackRef.current;
    if (track) {
      track.addEventListener('transitionend', handleTransitionEnd);
      return () => track.removeEventListener('transitionend', handleTransitionEnd);
    }
  }, [currentIndex]);

  useEffect(() => {
    updatePosition(currentIndex);
  }, [currentIndex, isMobile]);

  useEffect(() => {
    updatePosition(currentIndex); // Initial position
    // Set initial active text
    if (sliders[0]?.title) {
      handleButtonClick(sliders[0].title);
    }
  }, [sliders]);

  // Fallback UI if no sliders are provided
  if (!sliders.length) {
    return <div className="vertical-slider text-center">No sliders available</div>;
  }

  return (
    <div className="vertical-slider" style={{ position: 'relative' }}>
      <button className="nav-button prev" onClick={scrollUp} style={{ position: 'absolute' }}>
        <svg viewBox="0 0 24 24" width="24" height="24">
          <path d="M12 8l-6 6 1.41 1.41L12 10.83l4.59 4.58L18 14z" />
        </svg>
      </button>

      <div className="keynotes-slider" style={{ overflow: 'hidden' }}>
        <div className="slider-track" ref={trackRef}>
          {allItems.map((slider, idx) => (
            <div className="slider-item" key={`${slider.title}-${idx}`}>
              <button
                type="button"
                className={`btn w-100 ${slider.title === activeTextState ? 'active-slide' : ''}`}
                onClick={() => handleButtonClick(slider.title)}
                style={{
                  backgroundColor: slider.title === activeTextState ? '#fff' : '',
                  position: 'relative',
                  borderRadius: '5px',
                  padding: '17px 15px',
                  marginBottom: '20px',
                  fontSize: '1rem',
                  fontWeight: 600,
                  width: '92%',
                  textTransform: 'uppercase',
                  boxShadow: slider.title === activeTextState ? '0 4px 8px rgba(0, 123, 255, 0.2)' : 'none',
                }}
              >
                {slider.title}
              </button>
            </div>
          ))}
        </div>
      </div>

      <button className="nav-button next" onClick={scrollDown} style={{ position: 'absolute' }}>
        <svg viewBox="0 0 24 24" width="24" height="24">
          <path d="M12 16l-6-6 1.41-1.41L12 13.17l4.59-4.58L18 10z" />
        </svg>
      </button>
    </div>
  );
};

VerticalSlider.propTypes = {
  sliders: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
    })
  ).isRequired,
  setActiveText: PropTypes.func.isRequired,
};

export default VerticalSlider;